import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { getComicsByCategory, categories } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

export default function CategoryPage() {
  const { categoryId } = useParams<{ categoryId: string }>();
  const [comics, setComics] = useState<Comic[]>([]);
  const [activeCategory, setActiveCategory] = useState(categoryId || 'all');
  const [sortBy, setSortBy] = useState<'latest' | 'popular'>('latest');

  useEffect(() => {
    setActiveCategory(categoryId || 'all');
    const results = getComicsByCategory(categoryId || 'all');
    setComics(results);
    window.scrollTo({ top: 0, behavior: 'instant' as const });
  }, [categoryId]);

  const currentCategory = categories.find(c => c.id === activeCategory);

  const sortedComics = [...comics].sort((a, b) => {
    if (sortBy === 'latest') return new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime();
    return b.views - a.views;
  });

  const formatViews = (views: number): string => {
    if (views >= 10000) return `${(views / 10000).toFixed(1)}万`;
    return views.toString();
  };

  const categoryTitle = currentCategory ? `${currentCategory.name}韩漫` : '全部韩漫';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${categoryTitle} - 韩漫网`,
    description: `韩漫网${categoryTitle}分类页面，提供无删减韩漫下拉式免费在线阅读。`,
    url: `https://www.hanmanwang.com/category/${activeCategory}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '韩漫网',
      url: 'https://www.hanmanwang.com/',
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="px-4 md:px-6 py-6">
            <div className="max-w-7xl mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', to: '/' },
                  { label: '漫画分类', to: '/category/all' },
                  ...(activeCategory !== 'all' ? [{ label: currentCategory?.name || '' }] : []),
                ]}
              />

              <div className="mt-4 mb-8">
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                  {categoryTitle}
                </h1>
                <p className="text-sm text-foreground-500 mt-1">
                  共 {comics.length} 部韩漫作品 · 无删减下拉式免费在线阅读
                </p>
              </div>

              <div className="flex flex-col lg:flex-row gap-8">
                <aside className="lg:w-56 flex-shrink-0">
                  <div className="sticky top-24">
                    <h4 className="text-sm font-semibold text-foreground-900 mb-4">漫画分类</h4>
                    <div className="flex flex-col gap-1">
                      {categories.map((cat) => (
                        <Link
                          key={cat.id}
                          to={`/category/${cat.id}`}
                          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all duration-200 cursor-pointer ${
                            activeCategory === cat.id
                              ? 'bg-primary-50 text-primary-600 font-medium'
                              : 'text-foreground-600 hover:bg-background-100'
                          }`}
                        >
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className={`${cat.icon} text-xs`}></i>
                          </div>
                          <span>{cat.name}</span>
                          <span className="ml-auto text-xs text-foreground-400">{cat.count}</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                </aside>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-6">
                    <p className="text-sm text-foreground-500">
                      找到 <strong className="text-foreground-900">{comics.length}</strong> 部漫画
                    </p>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setSortBy('latest')}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                          sortBy === 'latest'
                            ? 'bg-primary-500 text-white'
                            : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                        }`}
                      >
                        最新更新
                      </button>
                      <button
                        onClick={() => setSortBy('popular')}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                          sortBy === 'popular'
                            ? 'bg-primary-500 text-white'
                            : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                        }`}
                      >
                        最受欢迎
                      </button>
                    </div>
                  </div>

                  {sortedComics.length === 0 ? (
                    <div className="text-center py-20">
                      <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                        <i className="ri-inbox-line text-4xl text-foreground-300"></i>
                      </div>
                      <p className="text-foreground-500">该分类暂无漫画</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                      {sortedComics.map((comic) => (
                        <Link
                          key={comic.id}
                          to={`/comic/${comic.id}`}
                          className="group cursor-pointer"
                        >
                          <div className="relative overflow-hidden rounded-lg aspect-[3/4] mb-3">
                            <img
                              src={comic.coverImage}
                              alt={`${comic.title} - 韩漫免费在线阅读`}
                              title={`${comic.title} - 韩漫网`}
                              className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                              loading="lazy"
                            />
                            <div className="absolute top-2 right-2">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                comic.status === '连载中'
                                  ? 'bg-accent-500/90 text-white'
                                  : 'bg-foreground-500/80 text-white'
                              }`}>
                                {comic.status}
                              </span>
                            </div>
                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent p-3">
                              <div className="flex items-center gap-1">
                                <div className="w-3 h-3 flex items-center justify-center">
                                  <i className="ri-star-fill text-xs text-accent-400"></i>
                                </div>
                                <span className="text-xs text-white font-medium">{comic.rating}</span>
                                <span className="text-xs text-white/50 ml-auto">{formatViews(comic.views)}阅读</span>
                              </div>
                            </div>
                          </div>
                          <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
                            {comic.title}
                          </h3>
                          <p className="text-xs text-foreground-500 mt-0.5">{comic.author}</p>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}